import * as React from 'react';
import classnames from 'classnames';

import Tooltip from 'components/Tooltip';
import Link from 'libs/router/Link';

import type {
  SideNavigationContainer,
  SideNavigationEntry,
  SideNavigationItem
} from 'components/side-navigation-v2/SideNavigation.type';

import './side-navigation.scss';

type Props = {
  items: Array<SideNavigationContainer>,
  onClick?: (trackingKey: string) => void
};

function renderNavItem(
  item: SideNavigationItem,
  onClick: Props['onClick'],
  currentLevel: number
): React.ReactNode {
  const wrapperClassNames = classnames('side-navigation__item', {
    'side-navigation__item--not-available': !item.isAvailable,
    [`side-navigation__item--level-${currentLevel}`]: currentLevel > 1
  });

  if (!item.isAvailable) {
    return (
      <Tooltip
        key={item.trackingKey}
        width="normal"
        message={item.customUnavailabilityTooltip ?? ''}
        classNameWrapper={wrapperClassNames}
      >
        {item.title}
      </Tooltip>
    );
  }

  return (
    <Link
      key={item.trackingKey}
      to={item.url}
      includes={item.includes}
      activeClassName="side-navigation__item--active"
      className={wrapperClassNames}
      onClick={() => onClick && onClick(item.trackingKey)}
    >
      {item.title}
    </Link>
  );
}

function renderItems(
  items: Array<SideNavigationEntry>,
  onClick: Props['onClick'],
  currentLevel: number = 0
): Array<React.ReactNode> {
  return items.map((item, index) => {
    if (item.type === 'container') {
      const subSectionClasses = classnames(
        'side-navigation__sub-section',
        `side-navigation__sub-section--level-${currentLevel}`
      );

      const classes = classnames('side-navigation__title', {
        [`side-navigation__title--level-${currentLevel}`]: currentLevel > 0
      });

      return (
        <div
          key={`${item.title}-${index}`}
          className={subSectionClasses}
        >
          <div className={classes}>
            {item.title}
          </div>
          {
            renderItems(item.children ?? [], onClick, currentLevel + 1)
          }
        </div>
      );
    }

    return renderNavItem(item, onClick, currentLevel);
  });
}

const SideNavigation = ({ items, onClick }: Props): JSX.Element => (
  <section className="side-navigation">
    {
      renderItems(items, onClick)
    }
  </section>
);

export default SideNavigation;
